import React, { useState, useEffect } from 'react';
import { fetchVenueStatistics } from '../../../services/Http/Api';
import DataTable from 'react-data-table-component';
import PeriodSelectBox from "../../global/PeriodSelectBox";
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MdAttachMoney, MdShoppingCart, MdReceipt, MdTrendingUp } from 'react-icons/md';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import { useCurrency } from '../../../context/CurrencyProvider';

const numberFormatter = (value) => new Intl.NumberFormat('en-US').format(value);

const VenueStatistics = ({ venueId }) => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [venueStats, setVenueStats] = useState([]);
    const [totals, setTotals] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [sortBy, setSortBy] = useState('date');
    const [sortDirection, setSortDirection] = useState('asc');
    const [selectedPeriod, setSelectedPeriod] = useState('yesterday');

    useEffect(() => {
        const loadVenueStats = async () => {
            setLoading(true);
            try {
                const data = await fetchVenueStatistics({
                    venueId,
                    period: selectedPeriod,
                    page: currentPage,
                    perPage,
                    sortBy,
                    sortDirection,
                });

                setTotals(data.totals);
                setVenueStats(data.stats.data);
                setTotalRows(data.stats.total);
            } catch (error) {
                console.error('Failed to load venue stats', error);
            } finally {
                setLoading(false);
            }
        };

        loadVenueStats();
    }, [selectedPeriod, currentPage, perPage, sortBy, sortDirection, venueId]);

    const handlePageChange = (page) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };
    const handleSort = (column, sortDirection) => {
        const columnField = column.key || 'date';
        setSortBy(columnField);
        setSortDirection(sortDirection);
    };

    const columns = [
        { name: t('Venues.VenueView.VenueStatistics.date'), selector: row => new Date(row.date).toLocaleDateString(), sortable: true, key: 'date' },
        {
            name: t('Venues.VenueView.VenueStatistics.total_price'),
            selector: (row) => formatResolvedAmount({
                eurAmount: row.total_price,
                localAmount: row.total_price_local,
                localCurrencyId: row.currency_id ?? totals?.currency_id,
            }),
            sortable: true,
            key: 'total_price',
        },
        {
            name: t('Venues.VenueView.VenueStatistics.total_revenue'),
            selector: (row) => formatResolvedAmount({
                eurAmount: row.total_revenue,
                localAmount: row.total_revenue_local,
                localCurrencyId: row.currency_id ?? totals?.currency_id,
            }),
            sortable: true,
            key: 'total_revenue',
        },
        { name: t('Venues.VenueView.VenueStatistics.total_quantity'), selector: row => numberFormatter(row.total_quantity), sortable: true, key: 'total_quantity' },
        { name: t('Venues.VenueView.VenueStatistics.transaction_count'), selector: row => numberFormatter(row.transaction_count), sortable: true, key: 'transaction_count' },
    ];

    const handlePeriodChange = (e) => {
        setSelectedPeriod(e.target.value);
    };

    return (
        <StatisticsContainer>
            <Header>
                <PeriodSelectBox value={selectedPeriod} onChange={handlePeriodChange} />
            </Header>

            <StatsGrid>
                <StatBox
                    icon={<MdAttachMoney className="icon" />}
                    label={t('Venues.VenueView.VenueStatistics.total_price')}
                    value={formatResolvedAmount({
                        eurAmount: totals?.total_price,
                        localAmount: totals?.total_price_local,
                        localCurrencyId: totals?.currency_id,
                    })}
                    color="primary"
                />

                <StatBox
                    icon={<MdShoppingCart className="icon" />}
                    label={t('Venues.VenueView.VenueStatistics.total_transactions')}
                    value={totals ? numberFormatter(totals.total_transactions) : 0}
                    color="success"
                />

                <StatBox
                    icon={<MdReceipt className="icon" />}
                    label={t('Venues.VenueView.VenueStatistics.total_revenue')}
                    value={formatResolvedAmount({
                        eurAmount: totals?.total_revenue,
                        localAmount: totals?.total_revenue_local,
                        localCurrencyId: totals?.currency_id,
                    })}
                    color="info"
                />

                <StatBox
                    icon={<MdTrendingUp className="icon" />}
                    label={t('Venues.VenueView.VenueStatistics.total_quantity')}
                    value={totals ? numberFormatter(totals.total_quantity) : 0}
                    color="warning"
                />
            </StatsGrid>

            <StyledCard noPadding>
                <DataTable
                    columns={columns}
                    data={venueStats}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onSort={handleSort}
                    sortServer
                    highlightOnHover
                    progressPending={loading}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    paginationDefaultPage={currentPage}
                    paginationPerPage={perPage}
                    customStyles={customStyles}
                />
            </StyledCard>
        </StatisticsContainer>
    );
};

export default VenueStatistics;

const StatisticsContainer = styled.div`
    background-color: transparent;
`;

const Header = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1.5rem;

    @media (max-width: 768px) {
        justify-content: center;
    }
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.25rem;
    margin-bottom: 1.5rem;
`;

const IconWrapper = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ color }) => {
        switch (color) {
            case 'primary': return 'rgba(112, 26, 130, 0.1)';
            case 'success': return 'rgba(16, 185, 129, 0.1)';
            case 'info': return 'rgba(59, 130, 246, 0.1)';
            case 'warning': return 'rgba(245, 158, 11, 0.1)';
            default: return 'rgba(112, 26, 130, 0.1)';
        }
    }};
    color: ${({ color }) => {
        switch (color) {
            case 'primary': return '#701a82';
            case 'success': return '#10b981';
            case 'info': return '#3b82f6';
            case 'warning': return '#f59e11';
            default: return '#701a82';
        }
    }};

    .icon {
        font-size: 1.5rem;
    }
`;

const StatInfo = styled.div`
    display: flex;
    flex-direction: column;
`;

const StatLabel = styled.span`
    font-size: 0.875rem;
    color: #64748b;
    font-weight: 500;
`;

const StatValue = styled.span`
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
`;

const StatBox = ({ icon, label, value, color }) => (
    <StyledCard style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: 0, padding: '1.25rem' }}>
        <IconWrapper color={color}>
            {icon}
        </IconWrapper>
        <StatInfo>
            <StatLabel>{label}</StatLabel>
            <StatValue>{value}</StatValue>
        </StatInfo>
    </StyledCard>
);

const customStyles = {
    table: {
        style: {
            backgroundColor: 'transparent',
        },
    },
    headRow: {
        style: {
            borderBottomColor: '#f3f4f6',
            minHeight: '48px',
        },
    },
    rows: {
        style: {
            minHeight: '64px',
            '&:not(:last-child)': {
                borderBottomColor: '#f3f4f6',
            },
        },
    },
    headCells: {
        style: {
            color: '#64748b',
            fontSize: '0.75rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
        },
    },
    cells: {
        style: {
            color: '#1e293b',
            fontSize: '0.875rem',
        },
    },
};
